<script setup lang="ts">
import GTag from '@consumer/components/GTag'
import { locations as locationsApi } from '@consumer/api'
import { faLocationDot } from '@fortawesome/pro-light-svg-icons'
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons'

const {
  modelValue = [],
  popularOptions = [],
  lockedItems = [],
  mode = 'add',
} = defineProps<{
  modelValue?: string[]
  popularOptions?: { label: string; value: string; imageUrl?: string }[]
  lockedItems?: string[]
  mode?: 'add' | 'edit'
}>()

const lockedSet = computed(() =>
  new Set(lockedItems.map(i => i.toLowerCase())),
)

function isLocked (value: string) {
  return lockedSet.value.has(value.toLowerCase())
}

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
}>()

const selected = computed(() => modelValue)

// Tracks imageUrl for every location we've ever seen — used to show images on selected chips
const imageCache = ref<Record<string, string | undefined>>({})

function cacheOptions (options: { value: string; imageUrl?: string }[]) {
  for (const o of options) {
    if (!(o.value in imageCache.value)) {
      imageCache.value[o.value] = o.imageUrl
    }
  }
}

watchEffect(() => {
  if (popularOptions.length) cacheOptions(popularOptions)
})

function add (value: string) {
  if (!value || selected.value.includes(value)) return
  emit('update:modelValue', [...selected.value, value])
}

function remove (value: string) {
  emit('update:modelValue', selected.value.filter(v => v !== value))
}

function isSelected (value: string) {
  return selected.value.includes(value)
}

function toggle (value: string) {
  if (isSelected(value)) remove(value)
  else add(value)
}

function onSelect (item: any) {
  cacheOptions([item])
  add(item.value ?? item.label)
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <GInputAutoSuggest
      v-if="mode === 'add'"
      name="locations"
      placeholder="Search for locations"
      title="Locations"
      :fetchOptions="locationsApi.search"
      @select="onSelect"
    >
      <template #itemPre="{ item }">
        <img
          v-if="item.imageUrl"
          :src="item.imageUrl"
          :alt="item.label"
          class="w-8 h-8 rounded object-cover shrink-0"
        >
        <div v-else class="w-8 h-8 rounded bg-surface-grey flex items-center justify-center shrink-0">
          <GIcon :icon="faLocationDot" class="text-v2-tertiary text-sm"/>
        </div>
      </template>
      <template #empty>
        No results found. Try searching for a nearby US city.
      </template>
    </GInputAutoSuggest>

    <div class="flex flex-wrap gap-2">
      <!-- Locked (verified) items — shown in both modes -->
      <GTag
        v-for="loc in lockedItems"
        :key="`locked-${loc}`"
        v-tooltip="'Verified by contact — cannot be removed'"
        :image="imageCache[loc]"
        :emoji="imageCache[loc] ? undefined : '📍'"
        :label="loc"
        variant="selected"
      >
        <template #right>
          <GIcon :icon="faBadgeCheck" class="verified-icon"/>
        </template>
      </GTag>

      <template v-if="mode === 'edit'">
        <p v-if="!selected.length && !lockedItems.length" class="text-sm text-v2-tertiary">
          No locations added yet. Use Add to search and add locations.
        </p>
        <GTag
          v-for="loc in selected.filter(l => !isLocked(l))"
          :key="loc"
          :image="imageCache[loc]"
          :emoji="imageCache[loc] ? undefined : '📍'"
          :label="loc"
          variant="selected"
          removable
          class="cursor-pointer"
          @remove="remove(loc)"
        />
      </template>
      <template v-else>
        <GTag
          v-for="loc in selected.filter(l => !isLocked(l) && !popularOptions.some(o => o.value === l))"
          :key="loc"
          :image="imageCache[loc]"
          :emoji="imageCache[loc] ? undefined : '📍'"
          :label="loc"
          variant="selected"
          class="qa-picker-option cursor-pointer"
          @click="remove(loc)"
        />
        <GTag
          v-for="option in popularOptions.filter(o => !isLocked(o.value))"
          :key="option.value"
          :image="option.imageUrl"
          :emoji="option.imageUrl ? undefined : '📍'"
          :label="option.label"
          :variant="isSelected(option.value) ? 'selected' : 'unselected'"
          class="qa-picker-option cursor-pointer"
          @click="toggle(option.value)"
        />
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.verified-icon {
  color: #40a648;
  font-size: 10px;
}
</style>
